import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { getSocketUrl } from "@/lib/socket";

const FORCE_LOGOUT_EVENT = "force_logout";

/**
 * Connects to the Socket.IO server with the current user id and listens for
 * `force_logout` (e.g. when an admin deactivates this user). On event, calls
 * onForceLogout(reason, disconnect) so the app can disconnect, clear session and redirect.
 */
export function useForceLogoutSocket(
  userId: string | null,
  onForceLogout: (reason: string, disconnect: () => void) => void
) {
  const socketRef = useRef<Socket | null>(null);
  const onForceLogoutRef = useRef(onForceLogout);
  onForceLogoutRef.current = onForceLogout;

  useEffect(() => {
    if (!userId) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const url = getSocketUrl();
    const socket = io(url, {
      query: { userId },
      autoConnect: true,
    });
    socketRef.current = socket;

    socket.on(FORCE_LOGOUT_EVENT, (payload: { reason?: string }) => {
      const reason =
        payload?.reason && typeof payload.reason === "string"
          ? payload.reason
          : "Your account has been deactivated.";
      const disconnect = () => {
        socket.removeAllListeners();
        socket.disconnect();
        socketRef.current = null;
      };
      onForceLogoutRef.current(reason, disconnect);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId]);
}
